import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  Tooltip,
} from 'recharts'
import useGetVsic from '../../../../../../common/category/useGetVsic'
import { Legend } from '../../../../../../common/chart/legend'
import { Element } from '../../../../../../common/html/element'
import { Loading } from '../../../../../../common/loading'
import { Notification } from '../../../../../../common/notification'
import { formatText, valToPercent } from '../../../../../../helpers/value'
import { selectUserType } from '../../../../../../layout/store/slice'
import { USER_TYPE } from '../../../../../../utils/authorization'
import {
  selectCompareIndustry,
  selectDefaultIndustry,
} from '../../../store/slice'
import {
  selectLoading,
  selectMatrixChart,
  selectMatrixTooltip,
} from '../../store/slice'
import classes from './index.module.scss'

const PerformanceMatrix = ({ isDynamicReport, settingDynamic = {} }) => {
  const loadingMatrixChart = useSelector(selectLoading('matrixChart'))
  const matrixChart = useSelector(selectMatrixChart)
  const loadingMatrixDisplay = useSelector(selectLoading('matrixDisplay'))
  const matrixTooltip = useSelector(selectMatrixTooltip)
  const { vsics } = useGetVsic()
  const vsicDefaultStore = useSelector(selectDefaultIndustry)
  const vsicCompareStore = useSelector(selectCompareIndustry)
  const { DEFAULT_INDUSTRY: vsicDefaultDyn, COMPARE_INDUSTRY: vsicCompareDyn } =
    settingDynamic
  const vsicDefault = isDynamicReport ? vsicDefaultDyn : vsicDefaultStore
  const vsicCompare = isDynamicReport ? vsicCompareDyn : vsicCompareStore

  const titleDefaultIndustry =
    vsics.find((item) => item.id === vsicDefault)?.name || ''
  const titleComparingIndustry =
    vsics.find((item) => item.id === vsicCompare)?.name || ''
  const userType = useSelector(selectUserType)

  const [checked, setChecked] = useState(['displayDefault', 'displayCompare'])

  const handleChangeChecked = (val) => {
    if (!val.length) {
      return
    }
    setChecked(val)
  }

  const legend = [
    {
      title: titleDefaultIndustry,
      dataKey: 'displayDefault',
      color: '#00A8B5',
      pieIndex: 0,
      hidden: !vsicDefault,
      isI18n: false,
      isCheckbox: userType?.userTypeId === USER_TYPE.PREMIUM,
    },
    {
      title: titleComparingIndustry,
      dataKey: 'displayCompare',
      color: '#E6964C',
      pieIndex: 1,
      hidden: !vsicCompare,
      isI18n: false,
      isCheckbox: userType?.userTypeId === USER_TYPE.PREMIUM,
    },
  ]

  const data = (matrixChart?.default || []).map((item) => {
    const matrixChartCompare = (matrixChart?.compare || []).find(
      (i) => i.ratioName === item.ratioName,
    )
    return {
      subject: item.group,
      displayDefault: item.ratioValue ?? 0,
      displayCompare: matrixChartCompare?.ratioValue ?? 0,
    }
  })

  const CustomTick = ({ payload, x, y, textAnchor, radius, ...rest }) => {
    return (
      <text
        x={x}
        y={payload.index === 3 ? y + 8 : y}
        textAnchor={textAnchor}
        fill="#808080"
      >
        {payload.value}
      </text>
    )
  }

  return (
    <div>
      <Element style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
        <Translate value="companyAnalysis.industryAnalysis.PERFORMANCE_MATRIX" />
      </Element>
      {loadingMatrixChart || loadingMatrixDisplay ? (
        <div style={{ height: 300, position: 'relative' }}>
          <Loading />
        </div>
      ) : !matrixChart?.default && !matrixChart?.compare ? (
        <div style={{ height: 300, position: 'relative' }}>
          <Notification />
        </div>
      ) : (
        <div id="performanceMatrixChartId" className={classes.backgroundChart}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div className="dFlex justifyContentCenter" style={{ flex: 0.6 }}>
              <RadarChart
                cx="50%"
                cy="50%"
                outerRadius="65%"
                width={450}
                height={300}
                data={data}
              >
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" tick={<CustomTick />} />
                <PolarRadiusAxis
                  angle={30}
                  domain={[-0.25, 1]}
                  tickCount={6}
                  tick={false}
                />
                {!!vsicCompare ? (
                  <>
                    {checked.includes('displayDefault') && (
                      <Radar
                        name="Display Default"
                        dataKey="displayDefault"
                        stroke="#00A8B5"
                        fill="#00A8B5"
                        fillOpacity={0.7}
                        isAnimationActive={false}
                      />
                    )}
                    {checked.includes('displayCompare') && (
                      <Radar
                        name="Display Compare"
                        dataKey="displayCompare"
                        stroke="#E6964C"
                        fill="#E6964C"
                        fillOpacity={0.7}
                        hide={!vsicCompare}
                        isAnimationActive={false}
                      />
                    )}
                  </>
                ) : (
                  <Radar
                    name="Display Default"
                    dataKey="displayDefault"
                    stroke="#00A8B5"
                    fill="#00A8B5"
                    fillOpacity={0.7}
                    isAnimationActive={false}
                  />
                )}
                <Tooltip
                  content={
                    <CustomTooltip
                      matrixTooltip={matrixTooltip}
                      titleDefaultIndustry={titleDefaultIndustry}
                      titleComparingIndustry={titleComparingIndustry}
                      checked={checked}
                    />
                  }
                />
              </RadarChart>
            </div>
            <div
              style={{
                flex: 0.4,
              }}
            >
              <Legend
                items={legend.map((item) => ({
                  ...item,
                  isCheckbox: !!vsicCompare && !isDynamicReport,
                  listCheckBox: checked,
                  setListCheckBox: handleChangeChecked,
                }))}
                className="dFlex flexColumn justifyContentCenter gap1 mr3"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PerformanceMatrix

const CustomTooltip = ({
  active,
  payload,
  label,
  matrixTooltip,
  titleDefaultIndustry,
  titleComparingIndustry,
  checked,
}) => {
  const locale = useSelector((state) => state.i18n.locale)

  if (active && payload && payload.length) {
    const groupName = payload?.[0]?.payload?.subject

    const defaultItem = matrixTooltip?.default?.find(
      (item) => item.group === groupName && checked.includes('displayDefault'),
    )
    const compareItem = matrixTooltip?.compare?.find(
      (item) => item.group === groupName && checked.includes('displayCompare'),
    )

    return (
      <div
        style={{
          backgroundColor: '#000000',
          opacity: 0.7,
          color: '#ffffff',
          padding: 12,
        }}
      >
        <div className="mb1">
          {groupName} ({formatText(defaultItem?.reportYear)})
        </div>
        <div>
          <table>
            <tbody>
              <tr>
                <td>
                  <Translate value="companyAnalysis.industryAnalysis.INDUSTRY_1" />
                </td>
                <td style={{ textAlign: 'end' }}>{defaultItem?.ratioName}</td>
              </tr>
              {defaultItem && (
                <tr>
                  <td style={{ maxWidth: 250 }}>{titleDefaultIndustry}</td>
                  <td style={{ textAlign: 'end' }}>
                    {valToPercent(defaultItem.ratioValue, locale)}
                  </td>
                </tr>
              )}
              {compareItem && (
                <tr>
                  <td style={{ maxWidth: 250 }}>{titleComparingIndustry}</td>
                  <td style={{ textAlign: 'end' }}>
                    {valToPercent(compareItem.ratioValue, locale)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }
  return null
}
